<template>
  <div id="cesium-container" ref="cesiumContainer">
    <div id="display-options-checkboxes" ref="displayOptionsCheckboxes">
      <span
        id="warning-text"
        v-if="this.nbOfSatsInConstellations > this.maxNbOfSatPoints"
      >
        Too many satellites to display
      </span>
      <v-checkbox
        label="Payloads"
        :disabled="
          this.nbOfSatsInConstellations <= this.maxNbOfPayloads ? false : true
        "
        v-model="showPayloads"
        :dark="true"
        :title="
          this.nbOfSatsInConstellations <= this.maxNbOfPayloads
            ? 'Display payloads'
            : 'Too many satellites to display payloads'
        "
        color="info"
      />
      <v-checkbox
        :label="'Show Timeline'"
        :dark="true"
        color="info"
        v-model="showTimeline"
      ></v-checkbox>
    </div>
    <cesium-lighting-button ref="cesiumLightingButton" />
    <cesium-recorder ref="cesiumRecorder" />
  </div>
</template>

<script>
import __ from 'lodash'
import { CesiumInitialization } from '../../components/ThreeD/CesiumInitialization'
import { AnalyticalOrbit } from '../../components/ThreeD/AnalyticalOrbit'
import { PointOfInterest } from '../../components/ThreeD/PointOfInterest'
import { ZoneOfInterest } from '../../components/ThreeD/ZoneOfInterest'
import { EarthMesh } from '../../components/ThreeD/EarthMesh'
import CesiumRecorder from '../../components/ThreeD/CesiumRecorder.vue'
import constellationUtils from '../../mixins/constellationUtils'
import { mapGetters } from 'vuex'
import * as Cesium from 'cesium'
import CesiumTools from '../../components/ThreeD/CesiumTools'
import CesiumConstellationsTools from '../../components/ThreeD/CesiumConstellationsTools'
import CesiumLightingButton from '../../components/ThreeD/CesiumLightingButton.vue'

export default {
  name: 'generation-cesium-viewer',
  mixins: [
    CesiumInitialization,
    constellationUtils,
    AnalyticalOrbit,
    ZoneOfInterest,
    PointOfInterest,
    EarthMesh
  ],
  props: [
    'pointOfInterest',
    'tracesResults',
    'name',
    'initialGuessResults',
    'merged',
    'isoStartDate',
    'poi',
    'meshes',
    'points'
  ],
  components: {
    'cesium-recorder': CesiumRecorder,
    'cesium-lighting-button': CesiumLightingButton
  },
  data() {
    return {
      index: 0,
      orbitTracesPC: null,
      pointsOfInterestPC: null,
      billboardCollection: null,
      labelCollection: null,
      payloadsDS: null,
      showPayloads: false,
      showTraces: true,
      colorsArray: [],
      positionsTimeStep: 0,
      maxNbOfPayloads: 200,
      maxNbOfSatPoints: 1.2e3,
      showTimeline: false
    }
  },
  computed: {
    ...mapGetters({
      currentResultsLog: 'currentResultsLog',
      currentMission: 'currentMission'
    }),
    orbitalPeriod() {
      const results = this.currentResultsLog.results
      let planeSMA = 0
      planeSMA =
        results.generatedConstellation
          .computedHomogeneousConstellationGeometries[0].plane.semiMajorAxis
      return 2 * Math.PI * Math.sqrt(planeSMA ** 3 / 3.986004415e14)
    },
    nbOfSatsInConstellations() {
      return this.getNumberOfItemsOfProperty('numberOfSpacecrafts')
    },
    nbOfPlanesInConstellations() {
      return this.getNumberOfItemsOfProperty('numberOfPlanes')
    },
    alongTrackDistance() {
      return this.tracesResults.payloads[0]
        .groundRangeAlongTrackOperatingAltitude
    },
    crossTrackDistance() {
      return this.tracesResults.payloads[0]
        .groundRangeCrossTrackOperatingAltitude
    }
  },
  watch: {
    tracesResults: {
      handler() {
        this.addTracesAndPayloads()
      },
      deep: true
    },
    showPayloads(show) {
      for (let i = 0; i < this.index; i++) {
        this.payloadsDS.entities.getById(i).ellipse.show = show
      }
    },
    isoStartDate() {
      this.setClock()
    },
    poi: {
      handler() {
        this.onPoiListChange(this.poi)
      },
      deep: true
    },
    points() {
      this.onMeshesListChange(this.meshes, this.points)
    },
    showTimeline(show) {
      this.toggleTimeline(show)
    }
  },

  async mounted() {
    this.initializeViewer(false, true, true)
    this.insertElementInToolbar(this.$refs.cesiumLightingButton.$el)
    this.insertElementInToolbar(this.$refs.cesiumRecorder.$el)
    this.insertElementInToolbar(this.$refs.displayOptionsCheckboxes)
    this.$refs.cesiumLightingButton.toggleLighting()
    this.deactivateControls()
    const defaultRectangle = Cesium.Rectangle.fromDegrees(-180, -90, 180, 90)
    this.viewer.scene.mode = Cesium.SceneMode.SCENE3D
    Cesium.Camera.DEFAULT_VIEW_RECTANGLE = defaultRectangle
    this.cameraShouldFly = false
    this.setClock()
    this.initializePrimitives()
    this.addTracesAndPayloads()
    this.hideSceneModePicker()
    this.initMeshesPC()
    this.initPOIDataSource()
    this.onPoiListChange(this.poi)
    this.onMeshesListChange(this.meshes)
    this.toggleTimeline(false)
    this.rotatingOrbitalPlanes = this.orbitTracesPC
    // this.viewer.scene.primitives.add(this.rotatingOrbitalPlanes)
    this.viewer.clock.onTick.addEventListener(this.computeIcrfToFixed)
    this.viewer.scene.postUpdate.addEventListener(this.makeEarthSpin)
    this.viewer.scene.morphComplete.addEventListener(this.onMorphComplete)
    this.computeIcrfToFixed(this.viewer.clock, true)
    this.viewer.scene.render(this.viewer.clock.currentTime)
  },

  methods: {
    setClock() {
      const startDate = Cesium.JulianDate.fromIso8601(this.isoStartDate)
      let stopDate = new Cesium.JulianDate()
      Cesium.JulianDate.addSeconds(startDate, this.orbitalPeriod, stopDate)
      this.setCesiumClock(
        startDate,
        stopDate,
        Math.round(this.orbitalPeriod / 30)
      )
    },
    initializePrimitives() {
      this.initTracesPC()
    },

    addTracesAndPayloads() {
      this.index = 0
      let traces = this.tracesResults.traces
      this.colorsArray = CesiumTools.createColorsArray(
        this.nbOfPlanesInConstellations
      )
      this.addAnalyticalTraces(traces)
      this.addPayloads(traces, 1)
    },

    addPayloads(traces, colorAlpha = 1) {
      if (
        !traces ||
        !traces.length ||
        this.nbOfSatsInConstellations > this.maxNbOfSatPoints
      ) {
        return
      }
      if (Cesium.defined(this.payloadsDS)) {
        this.viewer.dataSources.remove(this.payloadsDS)
        this.payloadsDS = null
      }
      this.payloadsDS = new Cesium.CustomDataSource()
      this.viewer.dataSources.add(this.payloadsDS)
      traces.forEach((constellation, constellationIndex) => {
        const startDate = Cesium.JulianDate.fromIso8601(this.isoStartDate)
        const [nbOfSats, intraPlanePhasing, interPlanePhasing] =
          this.getConstellationsPlaneParameters(constellationIndex)
        constellation.forEach((tracePositions, planeIndex) => {
          const length = tracePositions.length
          const timeStep = this.orbitalPeriod / length
          const color = Cesium.Color.fromAlpha(
            this.colorsArray[
              constellationIndex * constellation.length + planeIndex
            ],
            colorAlpha
          )
          for (let satIndex = 0; satIndex < nbOfSats; satIndex++) {
            const totalShift = CesiumConstellationsTools.computeShift(
              length,
              planeIndex,
              satIndex,
              intraPlanePhasing,
              interPlanePhasing
            )
            const satPositions =
              CesiumConstellationsTools.createShiftedPosArray(
                tracePositions,
                totalShift,
                timeStep
              )
            const sampledPositionProperty =
              CesiumConstellationsTools.createSampledPositionProperty(
                satPositions,
                startDate
              )
            this.addPayloadEntity(
              this.payloadsDS,
              sampledPositionProperty,
              color
            )
          }
        })
      })
    },

    addPayloadEntity(dataSource, sampledPositionProperty, color) {
      const payload = this.tracesResults.payloads[0]
      if (this.nbOfSatsInConstellations > this.maxNbOfPayloads) {
        this.addPointEntity(dataSource, {
          index: this.index,
          positions: sampledPositionProperty,
          fov: payload.centerAngleAlongTrackOperatingAltitude,
          color: color
        })
      } else {
        if (payload.fieldOfViewType.includes('CIRCULAR')) {
          this.addCircleEntity(dataSource, {
            index: this.index,
            positions: sampledPositionProperty,
            color: color
          })
        } else {
          this.addRectangularPolylineEntity(dataSource, {
            index: this.index,
            positions: sampledPositionProperty,
            color: color
          })
        }
      }
      this.index++
    },

    addPointEntity(dataSource, payloadParams) {
      dataSource.entities.add({
        id: payloadParams.index,
        position: payloadParams.positions,
        point: {
          pixelSize: 8,
          color: payloadParams.color,
          outlineWidth: 2,
          outlineColor: Cesium.Color.BLACK
        }
      })
    },

    addRectangularPolylineEntity(dataSource, payloadParams) {
      const color = payloadParams.color
      const material = new Cesium.ColorMaterialProperty(color)
      dataSource.entities.add(
        new Cesium.Entity({
          id: payloadParams.index,
          position: payloadParams.positions,
          polyline: new Cesium.PolylineGraphics({
            clampToGround: false,
            material: material,
            positions: new Cesium.CallbackProperty(
              () =>
                this.getRectangularPolylineCoordinates(payloadParams.positions),
              false
            ),
            width: 2,
            show: false
          }),
          point: {
            pixelSize: 8,
            color: color,
            outlineWidth: 2,
            outlineColor: Cesium.Color.BLACK
          }
        })
      )
    },

    getRectangularPolylineCoordinates(positions) {
      const retrieveSatPositions = (sampledPositionProperty) => {
        const currentTime = this.viewer.clock.currentTime
        const timePlusOne = new Cesium.JulianDate()
        Cesium.JulianDate.addSeconds(currentTime, 1, timePlusOne)
        const currSatPosFixed = sampledPositionProperty.getValue(currentTime)

        const nextSatPosFixed = sampledPositionProperty.getValue(timePlusOne)

        if (!currSatPosFixed || !nextSatPosFixed) {
          return [null, null]
        }

        return [currSatPosFixed, nextSatPosFixed]
      }

      const createDirectionVectors = () => {
        const alongTrackDirection = new Cesium.Cartesian3()
        const crossTrackDirection = new Cesium.Cartesian3()
        const downDirection = new Cesium.Cartesian3()

        Cesium.Cartesian3.subtract(
          nextSatPosFixed,
          currSatPosFixed,
          alongTrackDirection
        )
        // if really needed check ConstellationViewer.vue to re-implement it here
        // checkConstantALTDirection(id, alongTrackDirection)

        Cesium.Cartesian3.negate(currSatPosFixed, downDirection)

        Cesium.Cartesian3.cross(
          alongTrackDirection,
          currSatPosFixed,
          crossTrackDirection
        )
        return [alongTrackDirection, downDirection, crossTrackDirection]
      }

      const getNSD = () => {
        const alongTrackRay = new Cesium.Ray(
          currSatPosFixed,
          alongTrackDirection
        )

        const North = new Cesium.Cartesian3()
        const South = new Cesium.Cartesian3()
        const Down = new Cesium.Cartesian3()

        Cesium.Ray.getPoint(alongTrackRay, this.alongTrackDistance, North)
        Cesium.Ray.getPoint(alongTrackRay, -this.alongTrackDistance, South)
        return [North, South, Down]
      }

      const computeVertexPos = (ns, we) => {
        let posFixed = new Cesium.Cartesian3()
        let intPoint = new Cesium.Cartesian3()
        let crossTrackRay = null
        if (ns === 'North') {
          crossTrackRay = new Cesium.Ray(North, crossTrackDirection)
        } else {
          crossTrackRay = new Cesium.Ray(South, crossTrackDirection)
        }
        if (we === 'East') {
          Cesium.Ray.getPoint(crossTrackRay, this.crossTrackDistance, intPoint)
        } else {
          Cesium.Ray.getPoint(crossTrackRay, -this.crossTrackDistance, intPoint)
        }
        const downRay = new Cesium.Ray(intPoint, downDirection)
        const downDistance = Cesium.IntersectionTests.rayEllipsoid(
          downRay,
          this.viewer.scene.globe.ellipsoid
        ).start
        Cesium.Ray.getPoint(downRay, downDistance, posFixed)
        return posFixed
      }

      const [currSatPosFixed, nextSatPosFixed] = retrieveSatPositions(positions)

      // avoid crashing before the end of simulation
      if (!currSatPosFixed || !nextSatPosFixed) {
        return
      }

      const [alongTrackDirection, downDirection, crossTrackDirection] =
        createDirectionVectors()

      const [North, South, Down] = getNSD()

      const NW = computeVertexPos('North', 'West')
      const NE = computeVertexPos('North', 'East')
      const SE = computeVertexPos('South', 'East')
      const SW = computeVertexPos('South', 'West')
      const finalPoints = [SW, SE, NE, NW, SW]

      return finalPoints
    },

    addCircleEntity(dataSource, payloadParams) {
      const color = payloadParams.color
      dataSource.entities.add(
        new Cesium.Entity({
          id: payloadParams.index,
          position: payloadParams.positions,
          ellipse: {
            semiMinorAxis: this.crossTrackDistance,
            semiMajorAxis: this.crossTrackDistance,
            height: 0,
            outline: true,
            outlineColor: color,
            fill: false,
            show: false
          },
          point: {
            pixelSize: 8,
            color: color,
            outlineWidth: 2,
            outlineColor: Cesium.Color.BLACK
          }
        })
      )
    },

    getConstellationsPlaneParameters(constellationIndex) {
      const results = this.currentResultsLog.results
      let nbOfSats = 0
      let intraPlanePhasing = 0
      let interPlanePhasing = 0
      nbOfSats =
        results.generatedConstellation
          .computedHomogeneousConstellationGeometries[constellationIndex]
          .numberOfSpacecraftsPerPlane
      intraPlanePhasing =
        results.generatedConstellation
          .computedHomogeneousConstellationGeometries[constellationIndex]
          .intraPlanePhasing
      interPlanePhasing =
        results.generatedConstellation
          .computedHomogeneousConstellationGeometries[constellationIndex]
          .interPlanePhasing
      return [nbOfSats, intraPlanePhasing, interPlanePhasing]
    },

    getNumberOfItemsOfProperty(propertyName) {
      if (
        this.currentResultsLog.results.generatedConstellation
          .computedHomogeneousConstellationGeometries.length > 1
      ) {
        return this.currentResultsLog.results.generatedConstellation.computedHomogeneousConstellationGeometries.reduce(
          (previous, current) => previous[propertyName] + current[propertyName]
        )
      } else {
        return this.currentResultsLog.results.generatedConstellation
          .computedHomogeneousConstellationGeometries[0][propertyName]
      }
    }
  }
}
</script>

<style scoped>
#cesium-container {
  z-index: 2;
}
#display-options-checkboxes {
  z-index: 3;
  vertical-align: middle;
  display: inline-grid;
  grid-auto-flow: column;
}

#display-options-checkboxes .v-input--selection-controls.v-input {
  background-color: #303336;
  border: 1px solid #444;
  position: relative;
  display: inline-block;
  height: 32px;
  line-height: 32px;
  margin: 2px 3px;
  padding: 2px;
}

#warning-text {
  text-shadow: 0px 0px 1px rgba(200, 200, 200, 1);
  font-size: small;
  color: orange;
  margin: auto;
}
</style>
